import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { adminApi, type AdminProtocolDetail } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { X, Save, Building2, FileText, Calendar, Users, RotateCcw, Trash2 } from "lucide-react";
import { formatTime } from "@/lib/utils";

interface AdminProtocolDetailModalProps {
  protocolId: string;
  onClose: () => void;
  onUpdated: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  annual_meeting: "Årsstämma",
  board_meeting: "Styrelsemöte",
  constitutive_board: "Konstituerande styrelsemöte",
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Utkast",
  finalized: "Slutfört",
};

const statusBadgeStyles: Record<string, string> = {
  draft: "bg-yellow-100 text-yellow-700",
  finalized: "bg-green-100 text-green-700",
};

export function AdminProtocolDetailModal({ protocolId, onClose, onUpdated }: AdminProtocolDetailModalProps) {
  const [protocolDetail, setProtocolDetail] = useState<AdminProtocolDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUnfinalizing, setIsUnfinalizing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Edit form state (only for draft protocols)
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingTime, setMeetingTime] = useState("");
  const [meetingPlace, setMeetingPlace] = useState("");
  const [chairpersonName, setChairpersonName] = useState("");
  const [recordKeeperName, setRecordKeeperName] = useState("");

  useEffect(() => {
    loadProtocol();
  }, [protocolId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const loadProtocol = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminApi.getProtocol(protocolId);
      if (response.success && response.data) {
        const p = response.data;
        setProtocolDetail(p);
        setMeetingDate(p.meetingDate);
        setMeetingTime(p.meetingTime || "");
        setMeetingPlace(p.meetingPlace || "");
        setChairpersonName(p.chairpersonName || "");
        setRecordKeeperName(p.recordKeeperName || "");
      } else {
        setError(response.error?.message || "Kunde inte ladda protokoll");
      }
    } catch {
      setError("Kunde inte ansluta till servern");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (protocolDetail?.status !== "draft") {
      toast.error("Endast utkast kan redigeras");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const response = await adminApi.updateProtocol(protocolId, {
        meetingDate,
        meetingTime: meetingTime || undefined,
        meetingPlace: meetingPlace || undefined,
        chairpersonName: chairpersonName || undefined,
        recordKeeperName: recordKeeperName || undefined,
      });
      if (response.success) {
        onUpdated();
        toast.success("Protokollet har uppdaterats");
        onClose();
      } else {
        setError(response.error?.message || "Kunde inte uppdatera protokoll");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnfinalize = async () => {
    if (!confirm("Är du säker på att du vill återställa protokollet till utkast? Detta kan påverka kopplad årsredovisning.")) {
      return;
    }
    setIsUnfinalizing(true);
    try {
      const response = await adminApi.unfinalizeProtocol(protocolId);
      if (response.success) {
        onUpdated();
        toast.success("Protokollet har återställts till utkast");
        loadProtocol(); // Reload to get updated state
      } else {
        toast.error(response.error?.message || "Kunde inte återställa protokoll");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsUnfinalizing(false);
    }
  };

  const handleDelete = async () => {
    if (protocolDetail?.status !== "draft") {
      toast.error("Endast utkast kan tas bort. Återställ protokollet först.");
      return;
    }
    if (!confirm("Är du säker på att du vill ta bort detta protokoll permanent?")) {
      return;
    }
    setIsDeleting(true);
    try {
      const response = await adminApi.deleteProtocol(protocolId);
      if (response.success) {
        onUpdated();
        toast.success("Protokollet har tagits bort");
        onClose();
      } else {
        toast.error(response.error?.message || "Kunde inte ta bort protokoll");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  const isDraft = protocolDetail?.status === "draft";
  const isFinalized = protocolDetail?.status === "finalized";

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <FileText size={20} className="text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isDraft ? "Redigera protokoll" : "Protokolldetaljer"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error && !protocolDetail ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
            </div>
          ) : protocolDetail ? (
            <div className="space-y-5">
              {/* Company Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg border border-gray-200">
                    <Building2 size={20} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{protocolDetail.companyName}</p>
                    <p className="text-sm text-gray-500">{protocolDetail.organizationNumber}</p>
                  </div>
                </div>
              </div>

              {/* Protocol Type & Status */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Typ</p>
                  <p className="text-sm font-medium text-gray-900">
                    {TYPE_LABELS[protocolDetail.type] || protocolDetail.type}
                  </p>
                </div>
                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusBadgeStyles[protocolDetail.status]}`}>
                  {STATUS_LABELS[protocolDetail.status] || protocolDetail.status}
                </span>
              </div>

              {/* Fiscal Year (if linked to annual report) */}
              {protocolDetail.fiscalYearStart && protocolDetail.fiscalYearEnd && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <Calendar size={18} className="text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Räkenskapsår</p>
                    <p className="text-sm text-blue-700">
                      {new Date(protocolDetail.fiscalYearStart).toLocaleDateString("sv-SE")} – {new Date(protocolDetail.fiscalYearEnd).toLocaleDateString("sv-SE")}
                    </p>
                  </div>
                </div>
              )}

              {/* Unfinalize notice for finalized protocols */}
              {isFinalized && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <RotateCcw size={18} className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">Protokollet är slutfört</p>
                      <p className="text-xs text-amber-700 mt-1">
                        Slutförda protokoll kan inte redigeras direkt. Du kan återställa det till utkast för att göra ändringar.
                      </p>
                      <button
                        type="button"
                        onClick={handleUnfinalize}
                        disabled={isUnfinalizing}
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                      >
                        {isUnfinalizing ? <Spinner size="sm" /> : <RotateCcw size={14} />}
                        Återställ till utkast
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Editable fields (only for drafts) */}
              {isDraft && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mötesdatum</label>
                    <input
                      type="date"
                      value={meetingDate}
                      onChange={(e) => setMeetingDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mötestid</label>
                    <input
                      type="time"
                      value={meetingTime}
                      onChange={(e) => setMeetingTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mötesplats</label>
                    <input
                      type="text"
                      value={meetingPlace}
                      onChange={(e) => setMeetingPlace(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ordförande</label>
                    <input
                      type="text"
                      value={chairpersonName}
                      onChange={(e) => setChairpersonName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Protokollförare</label>
                    <input
                      type="text"
                      value={recordKeeperName}
                      onChange={(e) => setRecordKeeperName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Read-only info for finalized protocols */}
              {isFinalized && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Mötesdatum</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(protocolDetail.meetingDate).toLocaleDateString("sv-SE")}
                        {protocolDetail.meetingTime && ` kl ${formatTime(protocolDetail.meetingTime)}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Mötesplats</p>
                      <p className="text-sm font-medium text-gray-900">{protocolDetail.meetingPlace || "-"}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Ordförande</p>
                      <p className="text-sm font-medium text-gray-900">{protocolDetail.chairpersonName || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Protokollförare</p>
                      <p className="text-sm font-medium text-gray-900">{protocolDetail.recordKeeperName || "-"}</p>
                    </div>
                  </div>

                  {/* Approvers */}
                  {protocolDetail.approverNames && protocolDetail.approverNames.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <Users size={12} />
                        Justerare
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {protocolDetail.approverNames.map((name, idx) => (
                          <span key={idx} className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Metadata */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-xs text-gray-500">
                  <span className="font-medium">ID:</span> {protocolDetail.id}
                </p>
                {protocolDetail.annualReportId && (
                  <p className="text-xs text-gray-500">
                    <span className="font-medium">Årsredovisning:</span> {protocolDetail.annualReportId}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Skapad:</span>{" "}
                  {new Date(protocolDetail.createdAt).toLocaleString("sv-SE")}
                </p>
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Uppdaterad:</span>{" "}
                  {new Date(protocolDetail.updatedAt).toLocaleString("sv-SE")}
                </p>
                {protocolDetail.finalizedAt && (
                  <p className="text-xs text-green-600">
                    <span className="font-medium">Slutförd:</span>{" "}
                    {new Date(protocolDetail.finalizedAt).toLocaleString("sv-SE")}
                  </p>
                )}
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {protocolDetail && (
          <div className="flex items-center justify-between gap-3 p-5 border-t border-gray-200">
            <div>
              {isDraft && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  {isDeleting ? <Spinner size="sm" /> : <Trash2 size={16} />}
                  Ta bort
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onClose}>
                {isDraft ? "Avbryt" : "Stäng"}
              </Button>
              {isDraft && (
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <Spinner size="sm" /> : <Save size={16} />}
                  Spara ändringar
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
